import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Check, ChevronLeft } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import api from '../utils/api';

const MobileWaiverWizard = ({ trip, onClose, organizationName, customWaiverText, brandColor = '#007AFF', onSuccess }) => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [signaturePad, setSignaturePad] = useState(null);
  
  // Verification method: 'sms', 'email', or 'id'
  const [verificationMethod, setVerificationMethod] = useState('email'); // Default to email
  
  // SMS verification
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  
  // Email verification
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  
  const [sendingCode, setSendingCode] = useState(false);
  const [signing, setSigning] = useState(false);
  const [waiverViewed, setWaiverViewed] = useState(false);
  const [showWaiverText, setShowWaiverText] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false); // Track if verification send failed
  const [resendCooldown, setResendCooldown] = useState(0); // Cooldown timer for resend
  const [skippedVerification, setSkippedVerification] = useState(false); // Track if user skipped verification
  const [lastSendTime, setLastSendTime] = useState(0); // Track last send time to prevent spam
  const [smsFailed, setSmsFailed] = useState(false); // Track if SMS send failed
  const [skippedSMS, setSkippedSMS] = useState(false); // Track if user skipped SMS verification


  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendCode = async () => {
    // Check if cooldown is active (60 seconds since last send)
    const now = Date.now();
    const timeSinceLastSend = (now - lastSendTime) / 1000;
    if (timeSinceLastSend < 60 && lastSendTime > 0) {
      toast.error(`Please wait ${Math.ceil(60 - timeSinceLastSend)} seconds before sending again`);
      return;
    }

    setSendingCode(true);
    setVerificationFailed(false);
    
    try {
      if (verificationMethod === 'sms') {
        // SMS verification
        const formatted = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber}`;
        await api.post('/public/sms/send-code', { phoneNumber: formatted });
        toast.success('SMS code sent!');
        setPhoneNumber(formatted);
      } else if (verificationMethod === 'email') {
        // Email verification
        if (!email || !email.includes('@')) {
          toast.error('Please enter a valid email');
          setSendingCode(false);
          return;
        }
        await api.post('/public/email/send-code', { email: email });
        toast.success('Email code sent! Check your inbox.');
      }
      
      setResendCooldown(60);
      setLastSendTime(now);
      setSkippedVerification(false);
      setStep(5);
    } catch (error) {
      setVerificationFailed(true);
      toast.error(`Failed to send ${verificationMethod === 'sms' ? 'SMS' : 'email'} code`);
    } finally {
      setSendingCode(false);
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      // Verify code based on method
      let verified = false;
      let verifiedContact = '';
      
      if (verificationMethod === 'sms' && phoneNumber && smsCode) {
        const verifyRes = await api.post('/public/sms/verify-code', {
          phoneNumber: phoneNumber,
          code: smsCode
        });
        verified = verifyRes.data.valid;
        verifiedContact = phoneNumber;
        
        if (!verified) {
          toast.error('Invalid SMS code');
          setSigning(false);
          return;
        }
      } else if (verificationMethod === 'email' && email && emailCode) {
        const verifyRes = await api.post('/public/email/verify-code', {
          email: email,
          code: emailCode
        });
        verified = verifyRes.data.valid;
        verifiedContact = email;
        
        if (!verified) {
          toast.error('Invalid email code');
          setSigning(false);
          return;
        }
      } else if (verificationMethod === 'id') {
        // ID verification - no code needed
        verified = true;
        verifiedContact = 'ID Uploaded';
      }

      const deviceInfo = {};
      const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
      deviceInfo.userAgent = safe(() => navigator.userAgent);
      deviceInfo.platform = safe(() => navigator.platform);
      deviceInfo.screenResolution = safe(() => `${window.screen.width}x${window.screen.height}`);
      deviceInfo.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
      deviceInfo.language = safe(() => navigator.language);
      deviceInfo.hardwareConcurrency = safe(() => navigator.hardwareConcurrency);
      deviceInfo.deviceMemory = safe(() => navigator.deviceMemory);

      const pin = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '').slice(-3).padStart(3, '0');

      await api.post(`/public/trips/${trip.id}/sign-waiver?token=test123`, {
        signature: name.trim(),
        signatureName: name.trim(),
        verified: verified,
        verificationMethod: verificationMethod, // 'sms', 'email', or 'id'
        verifiedContact: verifiedContact, // phone number, email, or 'ID Uploaded'
        phoneNumber: verificationMethod === 'sms' ? phoneNumber : '',
        email: verificationMethod === 'email' ? email : '',
        deviceInfo: deviceInfo,
        pin: pin,
        signedAt: new Date().toISOString()
      });

      toast.success(`Waiver signed! PIN: ${pin}`);
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error('Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col overflow-y-auto">
      <div className="bg-gray-100 p-4 border-b sticky top-0 z-10">
        <div className="flex justify-between mb-2">
          <h3 className="font-semibold">Sign Waiver</h3>
          <span className="text-sm text-gray-500">Step {step > 5 ? 5 : step}/5</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="h-2 rounded-full transition-all" style={{ width: `${(Math.min(step, 5) / 5) * 100}%`, background: brandColor }} />
        </div>
      </div>

      <div className="flex-1 p-4 pb-24">
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Waiver</h2>
            
            {!showWaiverText ? (
              <div className="text-center py-8">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 text-left">
                  <p className="text-sm text-blue-900 font-semibold mb-3">Please review the waiver before continuing</p>
                  <p className="text-xs text-blue-800 leading-relaxed">
                    By proceeding, you acknowledge that we collect and store your signature, device location information, and timestamp to ensure legal enforceability of this waiver.
                  </p>
                </div>
                <Button 
                  onClick={() => {
                    setShowWaiverText(true);
                    setWaiverViewed(true);
                  }}
                  className="text-white text-lg py-6 px-8"
                  style={{ background: brandColor }}
                >
                  I Agree & View Waiver
                </Button>
              </div>
            ) : (
              <div>
                <div className="bg-gray-50 rounded-lg border p-4 text-sm text-gray-700 leading-relaxed space-y-3 mb-6 max-h-96 overflow-y-auto whitespace-pre-wrap break-words">
                  {customWaiverText || trip.waiverText || 'Waiver text not configured. Please contact the charter operator to set up waiver language.'}
                </div>
                <Button 
                  onClick={() => setStep(2)} 
                  disabled={!waiverViewed} 
                  className="w-full text-white text-lg py-6"
                  style={{ background: brandColor }}
                >
                  I Accept & Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold mb-2">Sign & Name</h2>
            <p className="text-sm text-gray-600 mb-4">Draw your signature (optional) and type your name</p>
            
            <div className="border-2 border-gray-300 rounded-lg bg-white mb-2">
              <SignatureCanvas ref={setSignaturePad} canvasProps={{ className: 'w-full h-32', style: { touchAction: 'none' } }} />
            </div>
            <button onClick={() => signaturePad?.clear()} className="mb-4 text-sm hover:underline" style={{ color: brandColor }}>Clear Signature</button>
            
            <Input 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Your full name *" 
              className="text-lg mb-6" 
            />
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="w-24"><ChevronLeft className="w-4 h-4" /></Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={name.trim().length < 2} 
                className="flex-1 text-white text-lg py-6" style={{ background: brandColor }}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-xl font-bold mb-4">Verify Your Identity</h2>
            
            {/* ============================================================
                🚨 SMS VERIFICATION TEMPORARILY DISABLED 🚨
                
                Tab selector is hidden until Twilio 10DLC verification completes.
                Currently only showing EMAIL verification.
                
                TO RE-ENABLE SMS VERIFICATION:
                1. Uncomment the tab selector div below (lines marked with "ENABLE SMS")
                2. Uncomment the conditional rendering block
                3. See /app/WAIVER_SMS_RENABLE.md for full instructions
                
                Last Updated: 2025-11-24
                Status: Waiting for 10DLC approval (est. 1 week)
            ============================================================ */}
            
            {/* ENABLE SMS: Uncomment this tab selector when 10DLC is approved
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setVerificationMethod('email')}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  verificationMethod === 'email'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={verificationMethod === 'email' ? { background: brandColor } : {}}
              >
                Email
              </button>
              <button
                onClick={() => setVerificationMethod('sms')}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  verificationMethod === 'sms'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={verificationMethod === 'sms' ? { background: brandColor } : {}}
              >
                SMS
              </button>
            </div>
            */}
            
            {/* TEMPORARY: Email only - no conditional rendering */}
            <p className="text-sm text-gray-600 mb-4">We'll send you a verification code</p>
            <Input 
              type="email" 
              value={email} 
              onChange={(e) => {
                setEmail(e.target.value);
                setVerificationFailed(false);
              }} 
              placeholder="your@email.com" 
              className="text-lg mb-4" 
              autoFocus 
            />
            
            {/* ENABLE SMS: Replace the above with this conditional block when SMS is ready
            {verificationMethod === 'email' ? (
              <>
                <p className="text-sm text-gray-600 mb-4">We'll send you a verification code</p>
                <Input 
                  type="email" 
                  value={email} 
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setVerificationFailed(false);
                  }} 
                  placeholder="your@email.com" 
                  className="text-lg mb-4" 
                  autoFocus 
                />
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">We'll send you a verification code</p>
                <Input 
                  type="tel" 
                  value={phoneNumber} 
                  onChange={(e) => {
                    setPhoneNumber(e.target.value.replace(/[^0-9+]/g, ''));
                    setVerificationFailed(false);
                  }} 
                  placeholder="Phone number" 
                  className="text-lg mb-4" 
                  autoFocus 
                />
              </>
            )}
            */}
            
            {/* Show error if verification failed */}
            {verificationFailed && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-900 font-medium">
                  Failed to send {verificationMethod === 'email' ? 'email' : 'SMS'} code
                </p>
                <p className="text-xs text-red-700 mt-1">
                  Check your {verificationMethod === 'email' ? 'email address' : 'phone number'} or try the options below
                </p>
              </div>
            )}
            
            {/* Can't Receive? fallback option */}
            <div className="mb-4">
              <button 
                onClick={() => {
                  setSkippedVerification(true);
                  setStep(6);
                }}
                className="w-full text-sm py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
              >
                {/* TEMPORARY: Hardcoded to "Email" until SMS is re-enabled */}
                Can't Receive Email?
                {/* ENABLE SMS: Replace above with dynamic text when SMS is ready:
                Can't Receive {verificationMethod === 'email' ? 'Email' : 'SMS'}?
                */}
              </button>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)} className="w-24"><ChevronLeft className="w-4 h-4" /></Button>
              <Button 
                onClick={handleSendCode} 
                disabled={sendingCode || (verificationMethod === 'email' ? !email.includes('@') : phoneNumber.length < 10)} 
                className="flex-1 text-white text-lg py-6" 
                style={{ background: brandColor }}
              >
                {sendingCode ? 'Sending...' : 'Send Code'}
              </Button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="text-xl font-bold mb-2">Enter Code</h2>
            <p className="text-sm text-gray-600 mb-4">
              Sent to {verificationMethod === 'email' ? email : phoneNumber}
            </p>
            <Input 
              value={verificationMethod === 'email' ? emailCode : smsCode} 
              onChange={(e) => {
                const code = e.target.value.replace(/\D/g, '').slice(0, 6);
                if (verificationMethod === 'email') {
                  setEmailCode(code);
                } else {
                  setSmsCode(code);
                }
              }} 
              placeholder="6-digit code" 
              className="text-3xl text-center font-mono tracking-widest mb-4" 
              maxLength={6} 
              autoFocus 
            />
            
            {/* Consistent styling for fallback options */}
            <div className="space-y-2 mb-6">
              <button 
                onClick={handleSendCode}
                disabled={resendCooldown > 0}
                className="w-full text-sm py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendCooldown > 0 ? `Resend available in ${resendCooldown}s` : 'Resend Code'}
              </button>
              <button 
                onClick={() => {
                  setSkippedVerification(true);
                  setStep(6);
                }}
                className="w-full text-sm py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
              >
                {/* TEMPORARY: Hardcoded to "Email" until SMS is re-enabled */}
                Can't Receive Email?
                {/* ENABLE SMS: Replace above with dynamic text when SMS is ready:
                Can't Receive {verificationMethod === 'email' ? 'Email' : 'SMS'}?
                */}
              </button>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)} className="w-24"><ChevronLeft className="w-4 h-4" /></Button>
              <Button 
                onClick={handleSign} 
                disabled={signing || (verificationMethod === 'email' ? emailCode.length !== 6 : smsCode.length !== 6)} 
                className="flex-1 bg-green-600 text-lg py-6"
              >
                {signing ? 'Signing...' : 'Sign Waiver'}
              </Button>
            </div>
          </div>
        )}

        {step === 6 && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-orange-600">ID Verification Required</h2>
            <div className="bg-orange-50 rounded-lg border border-orange-200 p-4 mb-6">
              <p className="text-sm text-gray-800 mb-3">
                No problem! Our staff will verify your identity when you arrive.
              </p>
              <p className="text-sm text-gray-800 mb-3">
                <strong>Staff may take a photo of your government-issued ID.</strong> This photo serves as verification of your identity for the signed waiver.
              </p>
              <p className="text-sm font-semibold text-orange-900">
                ⚠️ Please bring a valid government-issued ID (driver's license, passport, or state ID)
              </p>
            </div>
            <div className="flex gap-2">
              {/* Smart back button: go to Step 3 if skipped verification, Step 5 if came from code entry */}
              <Button 
                variant="outline" 
                onClick={() => setStep(skippedVerification ? 3 : 5)} 
                className="w-24"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button 
                onClick={async () => {
                  setSigning(true);
                  try {
                    const deviceInfo = {};
                    const safe = (fn) => { try { return fn(); } catch { return 'Unknown'; } };
                    deviceInfo.userAgent = safe(() => navigator.userAgent);
                    deviceInfo.platform = safe(() => navigator.platform);
                    deviceInfo.screenResolution = safe(() => `${window.screen.width}x${window.screen.height}`);
                    deviceInfo.timezone = safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

                    const pin = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '').slice(-3).padStart(3, '0');

                    await api.post(`/public/trips/${trip.id}/sign-waiver?token=test123`, {
                      signature: name.trim(),  // Legacy field
                      signatureName: name.trim(),  // Backend expects this field
                      smsVerified: false,
                      phoneNumber: phoneNumber || '',
                      deviceInfo: deviceInfo,
                      pin: pin,
                      idVerificationRequired: true,
                      signedAt: new Date().toISOString()
                    });

                    toast.success(`Waiver signed! PIN: ${pin}. Bring your ID!`);
                    window.location.reload();
                  } catch (error) {
                    toast.error('Failed to sign');
                  } finally {
                    setSigning(false);
                  }
                }}
                disabled={signing}
                className="flex-1 bg-orange-600 text-lg py-6"
              >
                {signing ? 'Signing...' : 'I Understand'}
              </Button>
            </div>
          </div>
        )}
      </div>

      <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl z-20">✕</button>
    </div>
  );
};

export default MobileWaiverWizard;